import type { RestartPlan, RestartPrepExternal, RestartPrepTerminal } from '@term-deck/core/restart-prep.js';

/**
 * Reinício preparado (28/09/2026) — o que a tela diz para cada estado. Puro, para o teste conferir a frase que o dono
 * lê sem montar a página.
 */

export type ChipTone = 'ok' | 'wait' | 'warn' | 'muted';

/** Evento de janela disparado pela tela depois de preparar/cancelar/reiniciar (o aviso da barra relê na hora). */
export const RESTART_PREP_CHANGED = 'term-deck:restart-prep-changed';

export function terminalChip(t: RestartPrepTerminal, planState: RestartPlan['state']): { text: string; tone: ChipTone } {
  if (planState === 'resuming' || planState === 'resumed') {
    switch (t.resume) {
      case 'sent': return { text: 'retomado com a mensagem', tone: 'ok' };
      case 'pending': return { text: 'aguardando o terminal ficar pronto', tone: 'wait' };
      case 'sending': return { text: 'enviando a mensagem', tone: 'wait' };
      case 'not_restored': return { text: 'não voltou no boot', tone: 'warn' };
      case 'timeout': return { text: 'voltou, mas não ficou pronto', tone: 'warn' };
      case 'uncertain': return { text: 'envio incerto (não repetido)', tone: 'warn' };
      case 'failed': return { text: `mensagem falhou${t.detail ? `: ${t.detail}` : ''}`, tone: 'warn' };
      default: break;
    }
    // Sem mensagem a mandar: diz se VOLTOU (antes dizia "reaberto" até para o terminal que ficou de fora).
    switch (t.back) {
      case 'missing': return { text: 'não voltou', tone: 'warn' };
      case 'failed': return { text: t.detail ?? 'não reabriu', tone: 'warn' };
      case 'reopened': return { text: 'reaberto pelo Tema', tone: 'ok' };
      case 'boot': return { text: 'reaberto', tone: 'ok' };
      default: return { text: planState === 'resuming' ? 'voltando' : 'reaberto', tone: planState === 'resuming' ? 'wait' : 'muted' };
    }
  }
  switch (t.interrupt) {
    case 'pending': return { text: 'trabalhando: vai receber ESC', tone: 'wait' };
    case 'confirmed': return { text: 'interrompido com ESC', tone: 'ok' };
    case 'unconfirmed': return { text: 'ESC sem confirmação', tone: 'warn' };
    case 'failed': return { text: t.detail ?? 'ESC não chegou', tone: 'warn' };
    case 'finished_naturally': return { text: 'terminou sozinho', tone: 'ok' };
    case 'kept': return { text: 'mantido de propósito', tone: 'muted' };
    default: return { text: 'ocioso', tone: 'muted' };
  }
}

export function externalChip(e: RestartPrepExternal, planState: RestartPlan['state']): { text: string; tone: ChipTone } {
  if (planState === 'resuming' || planState === 'resumed') {
    switch (e.reopen) {
      case 'opened': return { text: e.detail ?? 'reaberta', tone: 'ok' };
      case 'already_open': return { text: 'já estava aberta', tone: 'ok' };
      case 'blocked_on_screen': return { text: 'parada numa tela: precisa do dono', tone: 'warn' };
      case 'opening': return { text: 'abrindo', tone: 'wait' };
      case 'pending': return { text: 'aguardando', tone: 'wait' };
      case 'failed': return { text: `não reabriu${e.detail ? `: ${e.detail}` : ''}`, tone: 'warn' };
      default: return { text: e.detail ?? '—', tone: 'muted' };
    }
  }
  if (planState === 'cancelled') return { text: e.detail ?? 'sem mudança', tone: 'muted' };
  switch (e.interrupt) {
    case 'pending': return { text: 'trabalhando: vai receber ESC', tone: 'wait' };
    case 'confirmed': return { text: 'interrompida com ESC', tone: 'ok' };
    case 'unconfirmed': return { text: 'ESC sem confirmação', tone: 'warn' };
    case 'failed': return { text: 'ESC não chegou: o reinício encerra e ela volta', tone: 'warn' };
    case 'finished_naturally': return { text: 'terminou sozinha', tone: 'ok' };
    case 'kept': return { text: 'mantida de propósito', tone: 'muted' };
    default: return { text: 'ociosa', tone: 'muted' };
  }
}

/** Texto curto do aviso na barra; null = nada a mostrar. "Retomado" fica visível por 30 min. */
export function restartNavChip(plan: RestartPlan | null, ready: boolean, nowMs: number, caveat: string | null = null): { text: string; tone: ChipTone } | null {
  if (!plan) return null;
  if (plan.state === 'preparing') return { text: 'preparando reinício', tone: 'wait' };
  if (plan.state === 'ready') return ready ? (caveat ? { text: 'pronto, sem o Chrome', tone: 'warn' } : { text: 'pronto para reiniciar', tone: 'ok' }) : { text: 'reinício: algo voltou a trabalhar', tone: 'warn' };
  if (plan.state === 'resuming') return { text: 'retomando após reinício', tone: 'wait' };
  if (plan.state === 'resumed' && plan.resumedAtMs && nowMs - plan.resumedAtMs < 30 * 60_000) return { text: 'retomado após reinício', tone: 'ok' };
  return null;
}

export function clockBrasilia(ms: number): string {
  const d = new Date(ms - 3 * 3_600_000);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
